// =============================================================
// Web API binding — wraps the @perpetuum/core typed ApiClient with the
// browser session store. ONE ApiClient per app; the token lives in
// sessionStorage (cleared on tab close) and is read lazily by the client.
//
// Single source of truth = the core client (derived from the OpenAPI contract
// in @perpetuum/server). The web app adds only: token persistence + a small
// React context. No DTOs duplicated here.
// =============================================================

import { createApiClient, type ApiClient } from "@perpetuum/core";

const TOKEN_KEY = "perp.session.token";

/** Read the persisted session token (null when signed out). */
export function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Persist (or clear, with null) the session token. */
export function writeToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* sessionStorage unavailable (SSR/tests) — best-effort only */
  }
}

/**
 * Build the singleton ApiClient. `baseUrl` defaults to the same-origin
 * `/api/v1` (Vite proxies it in dev). The client reads the live token on every
 * request via `getToken`, so re-issuing a token after MFA needs no rebuild.
 */
export function buildApiClient(baseUrl = "/api/v1"): ApiClient {
  return createApiClient({ baseUrl, getToken: readToken });
}
